import type { GeoJSONSource, Map as MlMap } from 'maplibre-gl'

/**
 * Highlight marker layer — red halo + dot dropped at a search result location.
 *
 * Plan 02-02 stubs: `addHighlightLayer` registers the source + layers (idempotent).
 * `dropHighlightMarker` / `clearHighlightMarker` exported but real invocation
 * lands in Plan 02-03 (SearchBar onSelect).
 *
 * Per UI-SPEC §MapLibre Layer Styling §Source + Layer: highlight marker
 * (lines 427-453). Colors: `#D93025` day / `#FF6A5C` night at opacity 0.22
 * (halo), 0.0 stroke. Dot same color, radius 8, 2px white stroke.
 */

export const HIGHLIGHT_SRC_ID = 'highlight-marker-source'
export const HIGHLIGHT_HALO_ID = 'highlight-marker-halo'
export const HIGHLIGHT_DOT_ID = 'highlight-marker-dot'

function emptyFC() {
  return { type: 'FeatureCollection' as const, features: [] }
}

/**
 * Idempotent — safe to call after every `map.setStyle` via the `style.load`
 * handler in `swapTheme`. If the source already exists, bail.
 */
export function addHighlightLayer(map: MlMap): void {
  if (map.getSource(HIGHLIGHT_SRC_ID)) return

  map.addSource(HIGHLIGHT_SRC_ID, {
    type: 'geojson',
    data: emptyFC(),
  })

  map.addLayer({
    id: HIGHLIGHT_HALO_ID,
    type: 'circle',
    source: HIGHLIGHT_SRC_ID,
    paint: {
      'circle-radius': 18,
      'circle-color': [
        'case',
        ['==', ['get', 'theme'], 'night'],
        '#FF6A5C',
        '#D93025',
      ],
      'circle-opacity': 0.22,
      'circle-stroke-width': 0,
    },
  })

  map.addLayer({
    id: HIGHLIGHT_DOT_ID,
    type: 'circle',
    source: HIGHLIGHT_SRC_ID,
    paint: {
      'circle-radius': 8,
      'circle-color': [
        'case',
        ['==', ['get', 'theme'], 'night'],
        '#FF6A5C',
        '#D93025',
      ],
      'circle-stroke-width': 2,
      'circle-stroke-color': '#FFFFFF',
    },
  })
}

/**
 * Drop a single-point highlight marker at the given coordinate.
 * Call from Plan 02-03 SearchBar onSelect.
 */
export function dropHighlightMarker(
  map: MlMap,
  lngLat: [number, number],
  theme: 'day' | 'night',
): void {
  const src = map.getSource(HIGHLIGHT_SRC_ID) as GeoJSONSource | undefined
  if (!src) return
  src.setData({
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: lngLat },
        properties: { theme },
      },
    ],
  })
}

/**
 * Clear the highlight marker (user starts a new query, taps map, or selects
 * a different result).
 */
export function clearHighlightMarker(map: MlMap): void {
  const src = map.getSource(HIGHLIGHT_SRC_ID) as GeoJSONSource | undefined
  if (!src) return
  src.setData(emptyFC())
}
